import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useI18n } from "@/lib/i18n";
import { imgUrl } from "@/lib/api";

export const PhotoTour = ({ open, onOpenChange, photos, start }) => {
  const { t } = useI18n();
  const [idx, setIdx] = useState(null);
  useEffect(() => {
    if (open) setIdx(start);
  }, [open, start]);
  useEffect(() => {
    if (!open) return undefined;
    if (window.__lenis) window.__lenis.stop();
    return () => { if (window.__lenis) window.__lenis.start(); };
  }, [open]);
  useEffect(() => {
    if (idx === null) return undefined;
    const k = (e) => {
      if (e.key === "ArrowRight") setIdx((i) => (i + 1) % photos.length);
      if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + photos.length) % photos.length);
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [idx, photos.length]);
  const cur = idx !== null ? photos[idx] : null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="photo-tour-modal" className="h-[100dvh] max-w-none translate-y-[-50%] gap-0 overflow-hidden rounded-none border-0 bg-paper p-0 sm:rounded-none [&>button]:hidden">
        <DialogTitle className="sr-only">{t("l.tour")}</DialogTitle>
        <div className="flex h-[64px] items-center justify-between border-b border-ink/10 px-5">
          <p className="font-display text-2xl text-ink">{t("l.tour")}</p>
          <button data-testid="photo-tour-close" onClick={() => onOpenChange(false)} className="rounded-full p-2 hover:bg-ink/5"><X className="h-5 w-5" /></button>
        </div>
        <div data-lenis-prevent className="h-[calc(100dvh-64px)] overflow-y-auto px-4 py-6 sm:px-10">
          <div className="mx-auto max-w-5xl columns-1 gap-4 sm:columns-2">
            {photos.map((p, i) => (
              <figure key={i} className="mb-4 break-inside-avoid">
                <button data-testid={`photo-tour-item-${i}`} onClick={() => setIdx(i)} className="block w-full overflow-hidden rounded-2xl">
                  <img src={imgUrl(p.url)} alt={p.caption || ""} loading="lazy" className="w-full transition-transform duration-700 hover:scale-[1.03]" />
                </button>
                {p.caption && <figcaption className="mt-2 text-sm text-muted2">{p.caption}</figcaption>}
              </figure>
            ))}
          </div>
        </div>
        {cur && (
          <div data-testid="photo-viewer" className="fixed inset-0 z-[60] flex flex-col bg-[#14100b] text-paper">
            <div className="flex h-[64px] items-center justify-between px-5">
              <span className="font-mono text-xs tracking-[0.25em]">{idx + 1} / {photos.length}</span>
              <button data-testid="photo-viewer-close" onClick={() => setIdx(null)} className="rounded-full p-2 hover:bg-white/10"><X className="h-5 w-5" /></button>
            </div>
            <div className="relative flex flex-1 items-center justify-center px-4">
              <img key={idx} src={imgUrl(cur.url)} alt={cur.caption || ""} className="max-h-[78dvh] max-w-full rounded-lg object-contain" />
              <button data-testid="photo-viewer-prev" onClick={() => setIdx((idx - 1 + photos.length) % photos.length)} className="absolute left-3 rounded-full border border-white/30 bg-black/30 p-3 hover:bg-white/10"><ChevronLeft className="h-5 w-5" /></button>
              <button data-testid="photo-viewer-next" onClick={() => setIdx((idx + 1) % photos.length)} className="absolute right-3 rounded-full border border-white/30 bg-black/30 p-3 hover:bg-white/10"><ChevronRight className="h-5 w-5" /></button>
            </div>
            <p className="mx-auto max-w-2xl px-6 pb-8 pt-4 text-center text-sm text-[#E9DFD0]">{cur.caption}</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
